import React, { useState } from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { Chart } from '../types';
import ChartModal from './ChartModal';
import { chartApi } from '../services/api';

interface ChartCardProps {
  chart: Chart;
  categories: any[];
}

const ChartCard: React.FC<ChartCardProps> = ({ chart, categories }) => {
  const [showModal, setShowModal] = useState(false);
  const [detailedChart, setDetailedChart] = useState<Chart | null>(null);
  const [loading, setLoading] = useState(false);

  const handleViewClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const detailedData = await chartApi.getChart(chart.id);
      setDetailedChart(detailedData);
      setShowModal(true);
    } catch (error) {
      console.error('차트 상세 데이터 로드 실패:', error);
      // 실패 시 기본 차트 데이터로 모달 열기
      setDetailedChart(chart);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const cat = categories.find(c => c.name === chart.category);
  const color = cat?.color || '#6b7280';

  return (
    <>
      <Card
        className="theme-card border-0 shadow-lg h-100 cursor-pointer"
        style={{ borderRadius: '1.5rem', overflow: 'hidden', background: '#fff', boxShadow: '0 4px 24px 0 rgba(80, 120, 255, 0.08)' }}
        onClick={handleViewClick}
      >
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <span
              style={{
                display: 'inline-block',
                padding: '0.35em 0.65em',
                fontSize: '0.75rem',
                fontWeight: 600,
                lineHeight: 1,
                color: 'white',
                backgroundColor: color,
                borderRadius: '999px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginRight: '0.5em',
                marginBottom: '0.25em'
              }}
            >
              <i className="fas fa-tag me-1"></i>
              {chart.category}
            </span>
            <small className="text-muted">
              <i className="fas fa-clock me-1"></i>
              {new Date(chart.created_at).toLocaleDateString('ko-KR')}
            </small>
          </div>
          
          <Card.Title as="h6" className="mb-3 fw-bold">
            <span 
              className="text-decoration-none cursor-pointer chart-title"
              style={{ 
                lineHeight: '1.4', 
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
              onClick={handleViewClick}
            >
              {chart.title}
            </span>
          </Card.Title>
          
          <Card.Text className="text-muted small mb-3" style={{ lineHeight: '1.5' }}>
            {chart.description}
          </Card.Text>
          
          <div className="d-flex flex-wrap gap-2 mb-3">
            {chart.sub_category && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.25em 0.6em',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  lineHeight: 1,
                  color: 'var(--text-secondary)',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '999px',
                  marginRight: '0.25em',
                  marginBottom: '0.25em',
                  border: '1px solid var(--border-color)'
                }}
              >
                <i className="fas fa-layer-group me-1"></i>
                {chart.sub_category}
              </span>
            )}
            {chart.keywords && chart.keywords.length > 0 && (
              chart.keywords.slice(0, 2).map((keyword, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    padding: '0.25em 0.6em',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    lineHeight: 1,
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent',
                    borderRadius: '999px',
                    border: '1px solid var(--text-secondary)',
                    marginRight: '0.25em',
                    marginBottom: '0.25em'
                  }}
                >
                  <i className="fas fa-hashtag me-1"></i>
                  {keyword}
                </span>
              ))
            )}
          </div>
          
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="fas fa-eye text-muted me-2"></i>
              <small className="text-muted">조회수</small>
            </div>
            <Button 
              variant="outline-primary"
              size="sm"
              className="rounded-pill px-3"
              onClick={handleViewClick}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  로딩...
                </>
              ) : (
                <>
                  <i className="fas fa-arrow-right me-1"></i>
                  보기
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <ChartModal 
        chart={detailedChart}
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setDetailedChart(null);
        }}
      />
    </>
  );
};

export default ChartCard; 